import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { useMessageComposer } from 'stream-chat-react'
import { makeStyles } from '@material-ui/core/styles'
import Box from '@material-ui/core/Box'
import Button from '@material-ui/core/Button'
import IconButton from '@material-ui/core/IconButton'
import Typography from '@material-ui/core/Typography'
import CloseIcon from 'components/material-symbol/icons/Close'
import ExpandLess from 'components/material-symbol/icons/ExpandLess'
import ExpandMore from 'components/material-symbol/icons/ExpandMore'
import RetentionStatusChip from './retention-status-chip'
import { useChatFollowUpContext } from './api-hooks'

export const FOLLOW_UP_PARAM = 'followUp'
export const FOLLOW_UP_CUSTOM_FIELD = 'student_insights_follow_up'

export const followUpSearch = studentId =>
  `?${FOLLOW_UP_PARAM}=${encodeURIComponent(studentId)}`

// The student the coach came to follow up with, else the contact's most
// urgent student. Only students who need a follow-up get the helper.
export const pickInsight = (insights, requestedStudentId) => {
  const requested = insights.find(
    insight => insight.student.id === requestedStudentId,
  )
  if (requested?.needsAttention) return requested
  return insights.find(insight => insight.needsAttention) || null
}

const words = text => (text || '').toLowerCase().match(/[a-z0-9']{3,}/g) || []

// The message counts as the follow-up only while it keeps at least half of the
// suggestion's words, so a cleared draft followed by an unrelated message
// isn't logged as a follow-up.
export const followUpTag = (followUp, text) => {
  const draftWords = words(followUp.draft)
  const sentWords = new Set(words(text))
  const kept = draftWords.filter(word => sentWords.has(word)).length
  if (draftWords.length === 0 || kept / draftWords.length < 0.5) return null
  return {
    student_id: followUp.studentId,
    edited: (text || '').trim() !== followUp.draft.trim(),
  }
}

const useStyles = makeStyles(theme => ({
  root: {
    borderTop: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.grey[50],
    padding: theme.spacing(1.5, 2),
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  title: {
    fontWeight: 600,
    fontSize: '0.875rem',
  },
  reason: {
    color: theme.palette.text.secondary,
    fontSize: '0.8125rem',
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  facts: {
    display: 'grid',
    gridTemplateColumns: 'minmax(96px, max-content) 1fr',
    columnGap: theme.spacing(2),
    rowGap: theme.spacing(0.5),
    margin: theme.spacing(1, 0),
  },
  factLabel: {
    color: theme.palette.text.secondary,
    fontSize: '0.8125rem',
  },
  factValue: {
    fontSize: '0.8125rem',
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    flexWrap: 'wrap',
  },
  tip: {
    color: theme.palette.text.secondary,
    fontSize: '0.8125rem',
    flex: 1,
    minWidth: 200,
  },
}))

export function ChatFollowUpHelperView({
  insight,
  expanded,
  onToggle,
  onUseSuggestion,
  onDismiss,
}) {
  const classes = useStyles()

  return (
    <Box className={classes.root} data-testid="chat-follow-up-helper">
      <Box className={classes.header}>
        <RetentionStatusChip status={insight.status} />
        <Typography className={classes.title}>
          Follow up with {insight.student.firstName}
        </Typography>
        <Typography className={classes.reason} title={insight.reason}>
          {insight.reason}
        </Typography>
        <IconButton
          size="small"
          onClick={onToggle}
          aria-label={expanded ? 'Hide details' : 'Show details'}
        >
          {expanded ? (
            <ExpandMore fontSize="small" />
          ) : (
            <ExpandLess fontSize="small" />
          )}
        </IconButton>
        <IconButton
          size="small"
          onClick={onDismiss}
          aria-label="Close follow-up help"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
      {expanded ? (
        <>
          {insight.followUpContext.length > 0 ? (
            <Box className={classes.facts} component="dl" m={0}>
              {insight.followUpContext.map((fact, index) => (
                <React.Fragment key={`${fact.kind}-${index}`}>
                  <Typography component="dt" className={classes.factLabel}>
                    {fact.label}
                  </Typography>
                  <Typography component="dd" className={classes.factValue}>
                    {fact.value}
                  </Typography>
                </React.Fragment>
              ))}
            </Box>
          ) : null}
          <Box className={classes.footer}>
            <Typography className={classes.tip}>
              Add one thing you worked on together, like a drill or a goal. A
              personal line gets more replies than a template alone.
            </Typography>
            <Button
              size="small"
              variant="outlined"
              color="primary"
              onClick={onUseSuggestion}
              data-testid="chat-follow-up-use-suggestion"
            >
              Use Suggested Message
            </Button>
          </Box>
        </>
      ) : null}
    </Box>
  )
}

ChatFollowUpHelperView.propTypes = {
  insight: PropTypes.shape({
    status: PropTypes.string.isRequired,
    reason: PropTypes.string.isRequired,
    followUpContext: PropTypes.arrayOf(
      PropTypes.shape({
        kind: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired,
        value: PropTypes.string.isRequired,
      }),
    ).isRequired,
    student: PropTypes.shape({ firstName: PropTypes.string.isRequired })
      .isRequired,
  }).isRequired,
  expanded: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  onUseSuggestion: PropTypes.func.isRequired,
  onDismiss: PropTypes.func.isRequired,
}

// Sits above the chat's message box. Fills in the coach's template when they
// arrive to follow up with a student. Once the draft is in the message box it
// reports the active follow-up so the channel can tag the message it sends,
// even if the coach closes the helper first; unrelated messages sent before
// the draft is used stay untagged.
export default function ChatFollowUpHelper({
  contactExternalId,
  requestedStudentId,
  sentStudentIds,
  onActiveChange,
}) {
  const messageComposer = useMessageComposer()
  const [expanded, setExpanded] = useState(true)
  const [dismissed, setDismissed] = useState(false)
  const [draftUsedFor, setDraftUsedFor] = useState(null)
  const autofilled = useRef(false)
  const { data, error } = useChatFollowUpContext(contactExternalId)

  useEffect(() => {
    if (error) {
      window.rg4js &&
        window.rg4js('send', { error, tags: ['student-insights'] })
    }
  }, [error])

  const insights = data?.studentInsightsSummary?.forContact || []
  const insight = pickInsight(
    insights.filter(({ student }) => !sentStudentIds.includes(student.id)),
    requestedStudentId,
  )
  const pending = Boolean(insight)
  const visible = pending && !dismissed
  const studentId = insight?.student.id
  const draft = insight?.followUpDraft
  const draftUsed = Boolean(studentId) && draftUsedFor === studentId

  useEffect(() => {
    if (!pending || !draftUsed) return undefined
    onActiveChange({ studentId, draft })
    return () => onActiveChange(null)
  }, [pending, draftUsed, studentId, draft, onActiveChange])

  useEffect(() => {
    if (!visible || autofilled.current || studentId !== requestedStudentId) {
      return
    }
    autofilled.current = true
    if (draft && !messageComposer?.textComposer?.text) {
      messageComposer?.textComposer?.setText(draft)
      setDraftUsedFor(studentId)
    }
  }, [visible, studentId, requestedStudentId, draft, messageComposer])

  if (!visible) return null

  return (
    <ChatFollowUpHelperView
      insight={insight}
      expanded={expanded}
      onToggle={() => setExpanded(value => !value)}
      onUseSuggestion={() => {
        messageComposer?.textComposer?.setText(draft)
        setDraftUsedFor(studentId)
      }}
      onDismiss={() => setDismissed(true)}
    />
  )
}

ChatFollowUpHelper.propTypes = {
  contactExternalId: PropTypes.string.isRequired,
  requestedStudentId: PropTypes.string,
  sentStudentIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  onActiveChange: PropTypes.func.isRequired,
}
